import { useEffect, useState } from 'react';

import {
  deleteSMSProvider,
  getSMSProvidersList,
  setSMSProviders,
  SMSProviderFields,
  SMSProviderValues,
  updateSMSProvider
} from '../../api';
import {
  PreparedProviderValues,
  SelectedProviders
} from '../../api/configurations/providers/types';
import { Error } from '../../api/setup/getApiError';
import { useNotifications } from '../../hooks/useNotifications';
import { SmsProvidersHook } from './types';

export const useSmsProviders = (projectId: string): SmsProvidersHook => {
  const { showSuccessNotify, showErrorNotify } = useNotifications();

  const [fetching, setFetching] = useState<boolean>(true);
  const [updateFetching, setUpdateFetching] = useState<boolean>(false);
  const [data, setData] = useState<PreparedProviderValues | Record<string, never>>({});
  const [getDataError, setGetDataError] = useState<Error | null>(null);
  const [submitDataError, setSubmitDataError] = useState<Error | null>(null);

  const fetchProvidersData = async () => {
    try {
      const response = await getSMSProvidersList(projectId);
      const preparedData = response.reduce(
        (acc, item) => ({
          ...acc,
          [item.name]: item
        }),
        {}
      );
      setData(preparedData);
    } catch (err) {
      setGetDataError(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchProvidersData();
  }, []);

  const updateProvider = (values: SMSProviderValues, name: string) => {
    if (data[name]) {
      return updateSMSProvider(
        projectId,
        values,
        name,
        data[name].app_secret !== values.app_secret
      );
    }

    return setSMSProviders(projectId, { ...values, name });
  };

  const updateProviders = async (
    values: PreparedProviderValues,
    selectedFields: Partial<SelectedProviders>
  ) => {
    setUpdateFetching(true);
    setSubmitDataError(null);
    try {
      const newData:
        | ({
            [key: string]: SMSProviderFields;
          } & PreparedProviderValues)
        | Record<string, SMSProviderFields> = {};

      const requests = Object.entries(selectedFields).reduce((acc, [key, value]) => {
        if (!value) {
          return [...acc, deleteSMSProvider(projectId, key)];
        }

        newData[key] = { ...values[key] };

        return [
          ...acc,
          updateProvider(
            {
              ...values[key],
              name: key,
              is_enabled: !!values[key].is_enabled
            },
            key
          )
        ];
      }, []);
      await Promise.all(requests);

      setData(newData);
    } catch (err) {
      showErrorNotify(err);
      setSubmitDataError(err);
    } finally {
      showSuccessNotify();
      setUpdateFetching(false);
    }
  };

  return {
    providersFetching: fetching,
    providersUpdateFetching: updateFetching,
    getProvidersError: getDataError,
    saveProvidersError: submitDataError,
    providersData: data,
    updateProviders,
    updateProvider
  };
};
